import { logger, sanitizeError } from '@heidi/runtime/logger';
import { type components } from '@heidi/schema/ml-scribe';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';

import { type UserResponse } from '../../contracts';
import { GET_USER, UPDATE_AUTO_LINK_INTENT_MUTATION_KEY } from '../../contracts/keys';
import { updateAutoLinkIntent } from '../api/user';
import { USER_SETTINGS_TOASTS } from './userSettingsToasts.messages';

export { UPDATE_AUTO_LINK_INTENT_MUTATION_KEY };

type UpdateAutoLinkIntentRequest = components['schemas']['UpdateAutoLinkIntentRequest'];

export const useMutateUpdateAutoLinkIntent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [UPDATE_AUTO_LINK_INTENT_MUTATION_KEY],
    mutationFn: async (input: UpdateAutoLinkIntentRequest) => {
      await updateAutoLinkIntent(input);
    },
    meta: USER_SETTINGS_TOASTS.updateAutoLinkIntent,
    onMutate: async ({ intends_to_auto_link_sessions }) => {
      await queryClient.cancelQueries({ queryKey: [GET_USER] });

      const previousUser = queryClient.getQueryData<UserResponse>([GET_USER]);

      if (previousUser?.session_settings) {
        const updatedUser = produce(previousUser, (draft) => {
          if (draft.session_settings) {
            draft.session_settings.intends_to_auto_link_sessions =
              intends_to_auto_link_sessions;
          }
        });
        queryClient.setQueryData<UserResponse>([GET_USER], updatedUser);
      }

      return { previousUser };
    },
    onSuccess: async (_data, { intends_to_auto_link_sessions }) => {
      // Verify the write actually landed. The optimistic cache update means a
      // silent server drop leaves the UI showing "on" while the DB says "off",
      // with nothing recording the divergence — so force a refetch (not a bare
      // invalidate, which is a no-op without an active observer) and compare the
      // fresh stored value against what we sent.
      await queryClient.refetchQueries({ queryKey: [GET_USER] });
      const stored = queryClient.getQueryData<UserResponse>([GET_USER])?.session_settings
        ?.intends_to_auto_link_sessions;
      if (Boolean(stored) !== Boolean(intends_to_auto_link_sessions)) {
        logger.error(
          '[AutoLinkSessions] intent divergence: server stored value does not match the saved toggle',
          { sent: intends_to_auto_link_sessions, stored: stored ?? null }
        );
      }
    },
    onError: async (error, _variables, context) => {
      if (context?.previousUser) {
        queryClient.setQueryData([GET_USER], context.previousUser);
      }
      logger.error('[AutoLinkSessions] failed to update auto-link intent', {
        ...sanitizeError(error),
      });
      // Reconcile against server truth after the optimistic rollback.
      await queryClient.invalidateQueries({ queryKey: [GET_USER] });
    },
  });
};
